<template>
  <p-form-element class="p-select">
    <p-menu
      v-model="itemsVisible"
      :disabled="disabled || readonly"
      :open-on-click="false"
      :reference-element="getInputEl"
      min-width-of-reference
      bottom
      @input="onOpenCloseEvent($event)"
    >
      <template slot="activator">
        <p-text-field
          ref="text-field"
          :value="computedValue"
          v-bind="$props"
          role="combobox"
          :context-icon="computedContextIcon"
          :messages="innerMessages"
          :errors="innerErrors"
          :required="innerRequired"
          clearable-on-readonly
          :placeholder="computedPlaceholder"
          :aria-expanded="itemsVisible ? 'true' : 'false'"
          aria-autocomplete="false"
          :aria-controls="itemsVisible ? ('select-menu-items' + _uid) : undefined"
          :readonly="readonly || !canSearch"
          @focus="onDisplayFocus"
          @click="onExpanderClick(false)"
          @blur="onSearchFieldBlur"
          @input="onInput"
          @clear="onClearClicked"
          @keydown="onKeyDown"
          @keydown.tab="onTabDown"
          @keydown.space="onSpaceDown"
          @keydown.enter="onEnterDown"
          @keydown.escape.prevent="setItemsVisible(false)"
          @keydown.up.prevent="selectPreviousOption"
          @keydown.down.prevent="selectNextOption"
          v-on="{
            ...$listeners,
            // TODO: Figure out why this is necessary
            // If this is not added the $listeners contains a listener to the local function onSearchInput
            input: onInput
          }"
        >
          <template #actions>
            <p-button
              tabindex="-1"
              aria-hidden="true"
              tertiary
              dense
              :disabled="disabled || readonly"
              @click="onExpanderClick(true)"
            >
              <p-icon>
                {{ computedActionIcon }}
              </p-icon>
            </p-button>
          </template>
          <template v-for="(_, slot) of $scopedSlots" #[slot]="scope">
            <slot :name="slot" v-bind="scope" />
          </template>
        </p-text-field>
      </template>
      <p-surface :id="'select-menu-items' + _uid" role="listbox" class="dropdown">
        <slot name="prepend-list"></slot>
        <p-list-group>
          <p-list :dense="dense" :spacious="spacious">
            <div v-for="(item, index) in filteredItems" :key="index" class="p-select-list-entry">
              <slot name="prepend-list-entry"></slot>
              <slot
                name="list-entry"
                :class="getItemClass(index)"
                :detail="getItemDetail(item)"
                :caption="getItemDisplay(item)"
                :disabled="getItemDisabled(item)"
                :item="item"
                :index="index"
                :selected-item-index="selectedItemIndex"
              >
                <p-list-entry
                  :ref="'item' + index"
                  role="option"
                  :class="getItemClass(index)"
                  :title="getItemDetail(item)"
                  :disabled="getItemDisabled(item)"
                  @click="clickOption(item)"
                >
                  <div
                    :alt="getItemDisplay(item)"
                    class="list-item-content mx-2"
                  >
                    <slot
                      name="list-entry-content"
                      :class="getItemClass(index)"
                      :detail="getItemDetail(item)"
                      :caption="getItemDisplay(item)"
                      :disabled="getItemDisabled(item)"
                      :value="getItemValue(item)"
                      :item="item"
                      :index="index"
                      :selected-item-index="selectedItemIndex"
                    >
                      {{ getItemDisplay(item) }}
                    </slot>
                  </div>
                </p-list-entry>
              </slot>
              <slot name="append-list-entry"></slot>
            </div>
          </p-list>
        </p-list-group>
        <slot name="append-list"></slot>
      </p-surface>
    </p-menu>
  </p-form-element>
</template>

<script lang="ts">
  // TODO: Refactor p-select to be more accessible
  // Use this guide if it's helpful:
  // https://css-tricks.com/striking-a-balance-between-native-and-custom-select-elements/
  import Vue, { PropType } from 'vue';
  import PTextField from '../p-text-field/p-text-field.vue';
  import interactable from '../../mixins/interactable/interactable';
  import colorable from '../../mixins/colorable/colorable';
  import mixins from '../../mixins';
  import sizeable from '../../mixins/sizeable/sizeable';
  import validatable from '../../mixins/validatable/validatable';
  import _ from '../../utils';

  export interface pSelectItem {
    [prop: string | number]: any,
    caption?: string,
    detail?: string,
    sort?: number,
    disabled?: boolean,
  }

  export default mixins(interactable, colorable, sizeable, validatable).extend({
    mixins: [interactable, colorable, sizeable, validatable],
    props: {
      items: { default: () => [], type: Array as PropType<pSelectItem[]> },
      itemDisplay: { default: 'caption', type: [String, Number] },
      itemDetail: { default: 'detail', type: [String, Number] },
      itemValue: { default: 'id', type: [String, Number] },
      itemDisabled: { default: 'disabled', type: [String, Number] },
      canSearch: { default: false, type: Boolean },
      keepItemsOpen: { default: false, type: Boolean },
      performLocalSearch: { default: true, type: Boolean },
      search: { default: undefined, type: String },
      dense: { default: true, type: Boolean },
      ...((new PTextField()).$options.props),
      value: { default: undefined, type: [Number, String, Boolean, Object, Array] },
    },
    data: () => ({
      searchMode: false,
      selectedItem: undefined as Record<string, any> | undefined,
      selectedItemIndex: undefined as number | undefined,
      itemsVisible: false,
      filteredItems: [] as any[],
      internalPlaceholder: undefined as string | undefined,
      internalSearch: undefined as string | undefined,
      lastKeyPresses: [] as string[],
    }),
    computed: {
      computedContextIcon(): string {
        if (this.searchMode) {
          return 'search';
        }
        return (this as any).contextIcon;
      },
      computedPlaceholder(): string | undefined {
        if (this.searchMode && !this.canSearch) {
          return this.getItemDisplay(this.selectedItem);
        }
        return (this as any).placeholder;
      },
      computedActionIcon(): string {
        if (this.readonly || this.disabled) {
          return 'expand_more';
        }
        if ((this as any).actionIcon !== undefined) {
          return (this as any).actionIcon;
        }
        return this.itemsVisible ? 'expand_less' : 'expand_more';
      },
      computedValue(): string | undefined {
        if (this.searchMode) {
          return this.internalSearch;
        }
        return this.getItemDisplay(this.selectedItem);
      },
      computedCanSearch(): boolean {
        const canShowSearch = (this as any).canSearch;
        return canShowSearch && !this.readonly && !this.disabled;
      },
    },
    watch: {
      value: {
        immediate: true,
        handler() {
          this.setSelectedItem(this.value);
        },
      },
      items: {
        immediate: true,
        handler() {
          this.filteredItems = this.items;
          this.setSelectedItem(this.value);
        },
      },
    },
    methods: {
      onTabDown() {
        this.$nextTick(() => {
          this.setItemsVisible(false);
        });
      },
      onSpaceDown(event: KeyboardEvent) {
        if (this.searchMode) {
          return;
        }
        event.preventDefault();
        this.toggleItemsVisible((this.canSearch || !this.itemsVisible));
      },
      onEnterDown(event: KeyboardEvent) {
        event.preventDefault();
        if (this.canSearch) {
          this.resetSearch();
        }
        this.selectOption(this.selectedItem);
        this.toggleItemsVisible(false);
      },
      onKeyDown(event: KeyboardEvent) {
        if (this.canSearch) {
          return;
        }
        this.$debounce(() => {
          this.lastKeyPresses = [];
        }, 600, this.onKeyDown)();

        const { key } = event;

        // Native select cycles through items if key that was typed is same as last
        if (this.lastKeyPresses.length === 1 && key === this.lastKeyPresses[0]) {
          const allMatches = this.filteredItems
            .map((item, index) => ({ index, item }))
            .filter((item) => {
              const display = item.item?.[this.itemDisplay];
              const [displayFirstChar] = display;
              const isMatch = displayFirstChar?.toLowerCase() === key?.toLowerCase();
              const isDisabled = item.item?.[this.itemDisabled];
              return isMatch && !isDisabled;
            });
          let match = allMatches.find((item) => item.index > (this.selectedItemIndex ?? 0));
          if (!match) {
            [match] = allMatches;
          }
          if (match) {
            this.selectOption(match.item);
          }
        } else {
          this.lastKeyPresses.push(key);

          const itemMatch = this.filteredItems.find((item) => {
            const itemDisplay = item?.[this.itemDisplay]?.toLowerCase()?.replace(/\s/g, '');
            const isMatch = itemDisplay?.startsWith(this.lastKeyPresses.join(''));
            const isDisabled = item?.[this.itemDisabled];
            return isMatch && !isDisabled;
          });
          if (itemMatch) {
            this.selectOption(itemMatch);
          }
        }
      },
      getInputEl() {
        const textField = this.$refs['text-field'] as Vue;
        if (!textField) {
          return undefined;
        }
        return textField.$el.getElementsByClassName('p-text-field-input-wrapper')[0];
      },
      selectNextOption() {
        this.setItemsVisible(true);
        const startIndex = Math.min((this.selectedItemIndex ?? -1) + 1, this.filteredItems.length - 1);
        for (let i = startIndex; i < this.filteredItems.length; i += 1) {
          const item = this.filteredItems[i];
          if (item?.[this.itemDisabled]) {
            continue;
          } else {
            this.selectOption(this.filteredItems[i]);
            break;
          }
        }
      },
      selectPreviousOption() {
        this.setItemsVisible(true);
        const startIndex = Math.max((this.selectedItemIndex ?? 1) - 1, 0);
        for (let i = startIndex; i >= 0; i -= 1) {
          const item = this.filteredItems[i];
          if (item?.[this.itemDisabled]) {
            continue;
          } else {
            this.selectOption(this.filteredItems[i]);
            break;
          }
        }
      },
      onExpanderClick(force:boolean) {
        this.toggleItemsVisible(force ? !this.itemsVisible : (this.canSearch || !this.itemsVisible));
      },
      setItemsVisible(expanded: boolean) {
        if (this.itemsVisible !== expanded && this.canSearch) {
          this.resetSearch();
        }
        this.onOpenCloseEvent(expanded);

        if (this.readonly || this.disabled) {
          return;
        }
        this.itemsVisible = expanded;
      },
      resetSearch() {
        this.updateSearch(undefined);
        this.filteredItems = this.items;
      },
      toggleItemsVisible(isVisible:boolean) {
        this.setItemsVisible(isVisible);
      },
      onClearClicked() {
        this.selectedItem = undefined;
        this.internalSearch = undefined;
        this.onInput(undefined);
        this.$emit('input', undefined);
        this.$emit('display', this.getItemDisplay(undefined));
      },
      onOpenCloseEvent(isOpen: boolean) {
        this.$emit('openCloseEvent', isOpen);
      },
      clickOption(item: any) {
        this.selectOption(item);
        if (!this.keepItemsOpen) {
          this.itemsVisible = false;
          this.searchMode = false;
          this.resetSearch();
        }
      },
      selectOption(item: any) {
        if (this.readonly || this.disabled) {
          return;
        }
        if (this.getItemDisabled(item)) {
          return;
        }
        this.setSelectedItem(item);
        this.emitValue(this.selectedItem);
        this.$emit('selection', item, this.getItemValue(item), this.getItemDisplay(item), this.getItemDisabled(item));
        this.$emit('blur');
      },
      /** @param item can be either a value or an item object */
      setSelectedItem(item: any) {
        const value = _.isObject(item) ? item[this.itemValue] : item;
        if (value === null || value === undefined) {
          this.selectedItem = undefined;
          return;
        }
        const found = this.filteredItems.find((i) => i[this.itemValue] === value);
        if (found) {
          this.selectedItemIndex = this.filteredItems.indexOf(found);
          this.selectedItem = found;
        }
      },
      emitValue(entry: any) {
        const value = this.getItemValue(entry);
        const display = this.getItemDisplay(entry);
        this.$emit('input', value);
        this.$emit('display', display);
      },
      getItemDisabled(item: Record<string, any>) {
        if (item === undefined) {
          return true;
        }
        if (_.isObject(item)) {
          return item[this.itemDisabled] ?? false;
        }
        return false;
      },
      getItemClass(index: number) {
        return { 'p-select-item': true, active: this.selectedItemIndex === index };
      },
      getItemDisplay(item?: any) {
        if (item === undefined) {
          return '';
        }
        if (_.isObject(item)) {
          return item[this.itemDisplay];
        }
        return item;
      },
      getItemDetail(item: Record<string, any>) {
        if (item === undefined) {
          return '';
        }
        if (_.isObject(item)) {
          return item[this.itemDetail];
        }
        return item;
      },
      getItemValue(item: Record<string, any>) {
        if (_.isObject(item)) {
          return item[this.itemValue];
        }
        return item;
      },
      onDisplayFocus() {
        if (this.computedCanSearch) {
          this.resetSearch();
          this.searchMode = true;
        }
      },
      onInput(value: any) {
        if (this.searchMode) {
          this.onSearchInput(value);
        }
      },
      onSearchFieldBlur() {
        this.searchMode = false;
        if (!this.canSearch) {
          this.resetSearch();
        }
      },
      updateSearch(value?: string) {
        this.internalSearch = value;
        this.$emit('update:search', value);
      },
      onSearchInput(value: any) {
        this.itemsVisible = true;
        if (!value || !this.performLocalSearch) {
          this.filteredItems = this.items;
        } else {
          this.selectedItemIndex = 0;
          this.filteredItems = this.items.filter((item: any) => {
            const match = Object.keys(item).find((key) => {
              const itemDisplay = item[key];
              if (typeof itemDisplay === 'string') {
                const searchPieces = value.split(' ') as string[];
                const pieceMatch = searchPieces.find((piece) => {
                  return itemDisplay.toLowerCase().includes(piece.toLowerCase());
                });
                return !!pieceMatch;
              }
              return itemDisplay === this.search;
            });
            return !!match;
          });
          [this.selectedItem] = this.filteredItems;
        }
        this.updateSearch(value);
        // TODO: Put this into a config, or inject somehow?
        this.$debounce(() => {
          this.$emit('search', value);
        }, 250, this)();
      },
    },
  });
</script>
